import dotsHorizontal from '../../../public/dots-horizontal.svg';
import { useCancelMessage } from '../../hooks/useCancelMessage';
import { Message, MessageType, Room } from '@raysee/schema';
import { formatLimitLenghtText, renderTime } from '@raysee/utility';
import dayjs from 'dayjs';
import Image from 'next/image';
import { FC, VFC, useRef } from 'react';
import { Popup } from 'reactjs-popup';

export interface Sender {
  name: string;
  imageUrl: string;
}

export interface Props {
  room: Pick<Room, 'id' | 'userViewedTime' | 'createTime'>;
  message: Message & { canCall?: boolean };
  sender: Sender | null;
  role: 'teller' | 'user';
  isWaiting?: boolean;
  displayStatus?: boolean;
  fullSize?: boolean;
  onSend: (text: string) => void;
  spStaffRooms?: boolean;
  isPhone?: boolean;
}

export const Bubble: FC<Props> = ({
  room,
  message,
  sender,
  role,
  displayStatus = true,
  fullSize = false,
  children,
  spStaffRooms,
}) => {
  const popupRef = useRef({
    open: () => {},
    close: () => {},
    toggle: () => {},
  });
  const { execute: cancelMessage } = useCancelMessage(room.id);

  const openPopup = () => popupRef.current.open();
  const closePopup = () => popupRef.current.close();

  const canUsePopup =
    role === 'teller' &&
    !sender &&
    message.text !== '鑑定終了' &&
    message.type !== MessageType.Finished;

  const RedirectToTellerProfile = async () => {
    if (!message.senderId) return;
    const TellerProfileLink = `/tellers/${message.senderId}`;
    window.location.href = TellerProfileLink;
  };

  return (
    <div className="mx-2 my-4">
      {!!sender && (
        <div className="mb-[-4px] flex items-center">
          {sender.imageUrl && (
            <button onClick={RedirectToTellerProfile}>
              <Image src={sender.imageUrl} width="32" height="32" className="rounded-full" alt="" />
            </button>
          )}
          <p className="ml-2 inline text-xs">{sender.name}</p>
        </div>
      )}
      <div
        className={`my-1 flex items-end ${
          sender?.imageUrl && !fullSize && 'ml-6'
        } text-[14px] leading-[22px]`}
        style={{ flexDirection: sender ? 'row' : 'row-reverse' }}
      >
        <div
          className={fullSize ? '' : sender ? 'mr-2' : 'ml-2'}
          style={{
            maxWidth: fullSize ? '100%' : 'max(80%, 16rem)',
          }}
        >
          {children}
        </div>
        {displayStatus && !fullSize && (
          <div className="mb-2 flex w-16 flex-col" style={{ alignItems: sender ? 'start' : 'end' }}>
            {canUsePopup && (
              <Popup
                ref={popupRef}
                trigger={
                  <button style={{ fontSize: 8 }} className="mb-0.5" onClick={openPopup}>
                    <Image src={dotsHorizontal} width={20} height={20} alt="ドットアイコン" />
                  </button>
                }
                position="top right"
                disabled={!canUsePopup}
              >
                <SpeechBubble
                  buttons={[
                    {
                      title: '送信取り消し',
                      onClick: async () => {
                        if (
                          !window.confirm(
                            `以下のメッセージの送信を取り消しますがよろしいですか？\n\n${formatLimitLenghtText(
                              message.text,
                              30,
                            )}`,
                          )
                        ) {
                          closePopup();
                          return;
                        }
                        await cancelMessage(message);
                      },
                    },
                    {
                      title: 'キャンセル',
                      onClick: closePopup,
                    },
                  ]}
                />
              </Popup>
            )}
            {role === 'teller' &&
              !sender &&
              room.userViewedTime &&
              dayjs(room.userViewedTime).isAfter(message.createTime) && (
                <p style={{ fontSize: 8 }} className="mb-0.5">
                  既読
                </p>
              )}
            <p style={{ fontSize: spStaffRooms ? 10 : 6 }}>{renderTime(message.createTime)}</p>
          </div>
        )}
      </div>
    </div>
  );
};

interface SpeechBubbleProps {
  buttons: { title: string; onClick: () => void }[];
}

const SpeechBubble: VFC<SpeechBubbleProps> = ({ buttons }) => (
  <div
    style={{ marginBottom: -10 }}
    className="rounded-3xl rounded-br-none border-2 bg-white px-2 py-1 text-black drop-shadow-xl"
  >
    {buttons.map(({ title, onClick }, index) => (
      <div key={index}>
        {index !== 0 && <div className="border-t border-gray-300" />}
        <button className="block w-full px-2 py-1" onClick={onClick}>
          {title}
        </button>
      </div>
    ))}
  </div>
);
